import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, filter, map, of, take } from 'rxjs';
import { actions, feature } from '../+state';
import { SCOPE_LEVELS, SCOPE_LEVELS_ORDERED, ScopeLevel, queryForLevel } from '../utils';

export const SCOPED_PALETTE_INCLUDE_CHILDREN_KEY = 'scopedPaletteIncludeChildren';

// Book/toc levels: when set on a route, the resolver asks the API to also
// return palettes for the learning domains connected to the toc (or to the
// first-depth tocs of the book). Set on routes that style by LD class so
// cards/sections themed by a learning-domain class get their styling.
// Ignored at the method scope.
export const SCOPED_PALETTE_INCLUDE_LEARNING_DOMAINS_KEY = 'scopedPaletteIncludeLearningDomains';

// Toc-level only: when set on a route, the resolver asks the API to also
// return palettes for every ancestor toc of the requested toc (and, when
// combined with `INCLUDE_LEARNING_DOMAINS_KEY`, the LDs linked to those
// ancestors). Powers the deep cascade where a child toc's LD outranks its
// parent toc. Ignored at method/book scopes.
export const SCOPED_PALETTE_INCLUDE_TOC_ANCESTORS_KEY = 'scopedPaletteIncludeTocAncestors';

// Derived from `SCOPE_LEVELS_ORDERED` (most-specific first). The resolver picks
// the narrowest level present in the route params and dispatches a single load —
// the backend returns ancestor palettes alongside, so one request covers the
// chain. A level with no `routeParamAliases` can't be picked from a route, so
// it's filtered out — `learningDomain` for instance is never loaded directly.
const LEVELS_MOST_SPECIFIC_FIRST: readonly ScopeLevel[] = SCOPE_LEVELS_ORDERED.filter(
  (d) => d.routeParamAliases.length > 0
).map((d) => d.level);

function readNumericParam(route: ActivatedRouteSnapshot, key: string): number | undefined {
  const raw = route.paramMap.get(key);
  if (raw == null) return undefined;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readLevelId(route: ActivatedRouteSnapshot, level: ScopeLevel): number | undefined {
  for (const alias of SCOPE_LEVELS[level].routeParamAliases) {
    const value = readNumericParam(route, alias);
    if (value != null) return value;
  }

  return undefined;
}

function pickLevel(route: ActivatedRouteSnapshot): { level: ScopeLevel; id: number } | undefined {
  for (const level of LEVELS_MOST_SPECIFIC_FIRST) {
    const id = readLevelId(route, level);
    if (id != null) return { level, id };
  }

  return undefined;
}

export const resolveScopedPalette: ResolveFn<boolean> = (route) => {
  const store = inject(Store);

  const target = pickLevel(route);
  if (!target) return of(true);

  const includeChildren = !!route.data?.[SCOPED_PALETTE_INCLUDE_CHILDREN_KEY];
  const includeLearningDomains = !!route.data?.[SCOPED_PALETTE_INCLUDE_LEARNING_DOMAINS_KEY];
  const includeAncestors = !!route.data?.[SCOPED_PALETTE_INCLUDE_TOC_ANCESTORS_KEY];

  store.dispatch(
    actions.loadFor({
      level: target.level,
      id: target.id,
      includeChildren,
      includeLearningDomains,
      includeAncestors,
    })
  );

  // Gate on "attempted" (fetched OR failed), not "fetched", so a failing palette
  // API call does not hang router navigation — the page still renders, just
  // without a scoped palette.
  const query = queryForLevel(target.level, target.id, includeChildren, includeLearningDomains, includeAncestors);
  return store.select(feature.selectIsQueryAttempted(query)).pipe(
    filter(Boolean),
    take(1),
    map(() => true)
  ) as Observable<boolean>;
};
